import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ItemTile from '../Components/ItemTile/ItemTile';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { T } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { lazyFetch } from '@configuratorware/configurator-frontendgui/App/Utils/Request/lazyFetch';
import GlobalLoader from '@configuratorware/configurator-frontendgui/App/Shared/Containers/GlobalLoader';
import { withGlobalLoader } from '@configuratorware/configurator-frontendgui/App/Reducers/UI/Actions';
import DefaultThemeProvider from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/ThemeProvider';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles(
  (theme) => ({
    container: {
      marginTop: '30px',
      marginBottom: '30px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '70%',
      margin: 'auto',
      [theme.breakpoints.down('sm')]: {
        width: '95%',
      },
    },
    tileContainer: {
      display: 'flex',
      flexDirection: 'row',
      margin: 'auto',
    },
    categoryContainer: {
      display: 'flex',
      gap: '15px',
      width: '100%',
      marginTop: '40px',
      marginBottom: '30px',
      overflowX: 'auto',
      [theme.breakpoints.down('md')]: {
        marginTop: '20px',
      },
    },
    categoryButton: {
      backgroundColor: '#ededed',
      color: '#000',
      borderRadius: '50px',
      padding: '20px',
      '&.active': {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
      },
    },
    header: {
      minHeight: '64px',
      display: 'flex',
      justifyContent: 'center',
      paddingLeft: '25px',
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '10px',
    },
  }),
  { name: 'ItemOverview' }
);

export const ItemOverview = ({ pageSize }) => {
  const classes = useStyles();

  const [items, setItems] = useState([]);
  const dispatch = useDispatch();

  const fetchItems = async () => {
    const params = {
      offset: 0,
      limit: pageSize,
    };
    const response = await dispatch(
      withGlobalLoader(() =>
        lazyFetch({
          url: `items`,
          params,
        })
      )
    );
    return response.data;
  };

  const sortItemsBySequenceNumber = (items) => {
    return items.sort((a, b) => {
      const sequenceA =
        a.attributes.find((attr) => attr.identifier === 'sequencenumber')?.values[0]?.value ?? Infinity;
      const sequenceB =
        b.attributes.find((attr) => attr.identifier === 'sequencenumber')?.values[0]?.value ?? Infinity;
      return sequenceA - sequenceB;
    });
  };

  useEffect(() => {
    (async () => {
      const { data: items } = await fetchItems();
      const sortedItems = sortItemsBySequenceNumber(items);
      setItems(sortedItems);
    })();
  }, []);

  const renderItems = () => {
    if (items.length < 1) {
      return (
        <div>
          <Typography variant="body2">{T('itemOverview.noItems')}</Typography>
        </div>
      );
    } else {
      return (
        <div className={classes.tileContainer}>
          {items.map((item, index) => (
            <ItemTile
              title={item.title}
              image={item.thumbnail}
              linkTo={item.identifier}
              key={item.identifier + index}
              abstract={item.abstract}
            />
          ))}
        </div>
      );
    }
  };

  return (
    <DefaultThemeProvider>
      <div className={classes.container}>
        <Typography variant="h3">{T('itemOverview.title')}</Typography>
        {!!items.length && renderItems()}
        <GlobalLoader />
      </div>
    </DefaultThemeProvider>
  );
};

ItemOverview.propTypes = {
  pageSize: PropTypes.number,
};

ItemOverview.defaultProps = {
  pageSize: 8,
};

export default ItemOverview;
